import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Download, Upload, Check } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/tools/expenses")({
  head: () => ({ meta: [{ title: "Expenses & Mileage" }, { name: "robots", content: "noindex" }] }),
  component: ExpPage,
});

const IRS_RATE = 0.67;
const CATEGORIES = ["materials", "fuel", "mileage", "meals", "tools", "software", "vehicle", "office", "other"];

type E = {
  id: string; employee_name: string; project_id: string | null; project_name: string | null;
  category: string; vendor: string | null; description: string | null;
  amount: number; miles: number | null; mileage_rate: number | null;
  reimbursable: boolean; reimbursed: boolean; incurred_on: string; receipt_url: string | null; notes: string | null;
};

const emptyForm = (): Partial<E> => ({ employee_name: "", category: "materials", vendor: "", description: "", amount: 0, miles: null, mileage_rate: IRS_RATE, reimbursable: true, reimbursed: false, incurred_on: new Date().toISOString().slice(0, 10), receipt_url: null, notes: "" });

function ExpPage() {
  const [list, setList] = useState<E[]>([]);
  const [projects, setProjects] = useState<{ id: string; title: string }[]>([]);
  const [form, setForm] = useState<Partial<E>>(emptyForm());
  const [projectId, setProjectId] = useState("");
  const [filter, setFilter] = useState({ employee: "", from: "", to: "", category: "" });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const { data } = await supabase.from("expenses").select("*").order("incurred_on", { ascending: false });
    setList((data as any) || []);
    const { data: p } = await supabase.from("projects").select("id,title").order("created_at", { ascending: false });
    setProjects((p as any) || []);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const saved = localStorage.getItem("exp_emp"); if (saved) setForm(f => ({ ...f, employee_name: saved })); }, []);

  const submit = async () => {
    if (!form.employee_name) return toast.error("Employee required");
    if (form.category === "mileage") {
      if (!form.miles) return toast.error("Miles required");
      form.amount = (form.miles || 0) * (form.mileage_rate || IRS_RATE);
    }
    localStorage.setItem("exp_emp", form.employee_name);
    const proj = projects.find(p => p.id === projectId);
    const payload = { ...form, project_id: proj?.id || null, project_name: proj?.title || null };
    const { error } = await supabase.from("expenses").insert(payload as any);
    if (error) return toast.error(error.message);
    toast.success("Added"); setForm({ ...emptyForm(), employee_name: form.employee_name }); load();
  };

  const del = async (id: string) => { if (confirm("Delete?")) { await supabase.from("expenses").delete().eq("id", id); load(); } };
  const toggleReimbursed = async (e: E) => { await supabase.from("expenses").update({ reimbursed: !e.reimbursed }).eq("id", e.id); load(); };

  const uploadReceipt = async (file: File) => {
    setUploading(true);
    const path = `receipts/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
    const { error } = await supabase.storage.from("job-photos").upload(path, file);
    if (error) { toast.error(error.message); setUploading(false); return; }
    const { data } = await supabase.storage.from("job-photos").createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
    if (data?.signedUrl) setForm(f => ({ ...f, receipt_url: data.signedUrl }));
    setUploading(false);
  };

  const filtered = useMemo(() => list.filter(e => {
    if (filter.employee && !e.employee_name.toLowerCase().includes(filter.employee.toLowerCase())) return false;
    if (filter.category && e.category !== filter.category) return false;
    if (filter.from && e.incurred_on < filter.from) return false;
    if (filter.to && e.incurred_on > filter.to) return false;
    return true;
  }), [list, filter]);

  const totals = useMemo(() => {
    let amt = 0, miles = 0, reimb = 0, unpaid = 0;
    const byCat: Record<string, number> = {};
    for (const e of filtered) {
      amt += Number(e.amount) || 0; miles += Number(e.miles) || 0;
      if (e.reimbursable) reimb += Number(e.amount) || 0;
      if (e.reimbursable && !e.reimbursed) unpaid += Number(e.amount) || 0;
      byCat[e.category] = (byCat[e.category] || 0) + (Number(e.amount) || 0);
    }
    return { amt, miles, reimb, unpaid, byCat };
  }, [filtered]);

  const exportCSV = () => {
    const rows = [["Date", "Employee", "Category", "Project", "Vendor", "Description", "Amount", "Miles", "Reimbursable", "Reimbursed", "Receipt", "Notes"]];
    for (const e of filtered) rows.push([e.incurred_on, e.employee_name, e.category, e.project_name || "", e.vendor || "", e.description || "", (e.amount||0).toFixed(2), (e.miles||0).toString(), e.reimbursable?"Y":"N", e.reimbursed?"Y":"N", e.receipt_url || "", e.notes || ""]);
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `expenses-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-bold">Expenses & Mileage</h1>

      <div className="mt-6 rounded-lg border bg-card p-4">
        <h2 className="font-semibold mb-3 flex items-center gap-2"><Plus className="h-4 w-4" />Log Entry</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <div><label className="text-xs text-muted-foreground">Employee</label><Input value={form.employee_name || ""} onChange={e => setForm({ ...form, employee_name: e.target.value })} /></div>
          <div><label className="text-xs text-muted-foreground">Date</label><Input type="date" value={form.incurred_on || ""} onChange={e => setForm({ ...form, incurred_on: e.target.value })} /></div>
          <div><label className="text-xs text-muted-foreground">Category</label>
            <select className="w-full rounded border border-input bg-background p-2" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select>
          </div>
          <div><label className="text-xs text-muted-foreground">Project (optional)</label>
            <select className="w-full rounded border border-input bg-background p-2" value={projectId} onChange={e => setProjectId(e.target.value)}>
              <option value="">— None —</option>{projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </div>
          <div><label className="text-xs text-muted-foreground">Vendor</label><Input value={form.vendor || ""} onChange={e => setForm({ ...form, vendor: e.target.value })} /></div>
          <div><label className="text-xs text-muted-foreground">Description</label><Input value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          {form.category === "mileage" ? (
            <>
              <div><label className="text-xs text-muted-foreground">Miles</label><NumberInput step="0.1" value={form.miles ?? 0} onValueChange={n => setForm({ ...form, miles: n })} /></div>
              <div><label className="text-xs text-muted-foreground">Rate ($/mi)</label><NumberInput step="0.01" value={form.mileage_rate ?? IRS_RATE} onValueChange={n => setForm({ ...form, mileage_rate: n })} /></div>
              <div><label className="text-xs text-muted-foreground">Auto-calc</label><Input readOnly value={`$${((form.miles || 0) * (form.mileage_rate || IRS_RATE)).toFixed(2)}`} /></div>
            </>
          ) : (
            <div><label className="text-xs text-muted-foreground">Amount ($)</label><NumberInput step="0.01" value={form.amount ?? 0} onValueChange={n => setForm({ ...form, amount: n })} /></div>
          )}
          <div className="flex items-end gap-3">
            <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={form.reimbursable} onChange={e => setForm({ ...form, reimbursable: e.target.checked })} />Reimbursable</label>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Receipt</label>
            <label className="flex items-center gap-1 rounded border border-input px-3 py-1.5 text-sm cursor-pointer hover:bg-accent"><Upload className="h-4 w-4" />{form.receipt_url ? "Replace" : "Upload"}<input type="file" accept="image/*,application/pdf" className="hidden" onChange={e => e.target.files?.[0] && uploadReceipt(e.target.files[0])} /></label>
            {uploading && <p className="text-xs mt-1">Uploading…</p>}
            {form.receipt_url && <a href={form.receipt_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline">View</a>}
          </div>
        </div>
        <Textarea placeholder="Notes" className="mt-3" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} />
        <div className="mt-3 flex justify-end"><Button onClick={submit}>Add Expense</Button></div>
      </div>

      <div className="mt-6 flex flex-wrap items-end gap-3">
        <div><label className="text-xs text-muted-foreground">Employee</label><Input value={filter.employee} onChange={e => setFilter({ ...filter, employee: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Category</label>
          <select className="rounded border border-input bg-background p-2" value={filter.category} onChange={e => setFilter({ ...filter, category: e.target.value })}>
            <option value="">All</option>{CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div><label className="text-xs text-muted-foreground">From</label><Input type="date" value={filter.from} onChange={e => setFilter({ ...filter, from: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">To</label><Input type="date" value={filter.to} onChange={e => setFilter({ ...filter, to: e.target.value })} /></div>
        <Button variant="outline" onClick={exportCSV}><Download className="h-4 w-4 mr-1" />Export CSV</Button>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-4">
        <div className="rounded-lg border p-3 bg-card"><p className="text-xs text-muted-foreground">Total</p><p className="font-display text-2xl font-bold">${totals.amt.toFixed(2)}</p></div>
        <div className="rounded-lg border p-3 bg-card"><p className="text-xs text-muted-foreground">Miles</p><p className="font-display text-2xl font-bold">{totals.miles.toFixed(1)}</p></div>
        <div className="rounded-lg border p-3 bg-card"><p className="text-xs text-muted-foreground">Reimbursable</p><p className="font-display text-2xl font-bold">${totals.reimb.toFixed(2)}</p></div>
        <div className="rounded-lg border p-3 bg-card"><p className="text-xs text-muted-foreground">Unpaid</p><p className="font-display text-2xl font-bold text-amber-600">${totals.unpaid.toFixed(2)}</p></div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground border-b">
            <tr><th className="p-2">Date</th><th className="p-2">Employee</th><th className="p-2">Category</th><th className="p-2">Vendor / Description</th><th className="p-2 text-right">Amount</th><th className="p-2 text-right">Miles</th><th className="p-2">Reimb</th><th></th><th></th></tr>
          </thead>
          <tbody>
            {filtered.map(e => (
              <tr key={e.id} className="border-b">
                <td className="p-2">{e.incurred_on}</td>
                <td className="p-2">{e.employee_name}</td>
                <td className="p-2 capitalize">{e.category}</td>
                <td className="p-2">{e.vendor}{e.vendor && e.description ? " · " : ""}{e.description}</td>
                <td className="p-2 text-right">${(e.amount||0).toFixed(2)}</td>
                <td className="p-2 text-right">{e.miles ? e.miles.toFixed(1) : "—"}</td>
                <td className="p-2">{e.reimbursable ? (
                  <button onClick={() => toggleReimbursed(e)} className={`text-xs rounded-full px-2 py-0.5 ${e.reimbursed ? 'bg-emerald-500/15 text-emerald-600' : 'bg-amber-500/15 text-amber-600'}`}>{e.reimbursed ? <><Check className="inline h-3 w-3" /> Paid</> : "Unpaid"}</button>
                ) : "—"}</td>
                <td className="p-2">{e.receipt_url && <a href={e.receipt_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline">Receipt</a>}</td>
                <td className="p-2"><button onClick={() => del(e.id)} className="text-destructive"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={9} className="p-4 text-center text-muted-foreground">No expenses.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
